import {
    AccountEnvironment,
    CredentialContainer,
    CredentialMetadataRepresentation,
    getCredentials,
    useEnvironment,
    usePromise,
} from "@keycloak/keycloak-account-ui";
import { Page, Button, TextSkeleton, CardSkeleton, Dialog } from "../components";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { showSuccessToast, showErrorToast } from "../utils";
import { notifyAccountReady } from "../utils/startupOverlay";
import {
    Key,
    Smartphone,
    CreditCard,
    FileText,
    CheckCircle,
    Plus,
    Edit,
    Trash2,
    AlertTriangle,
    Shield
} from "lucide-react";

export const SigningIn = () => {
    const { t } = useTranslation();
    const context = useEnvironment<AccountEnvironment>();
    const [credentials, setCredentials] = useState<CredentialContainer[]>([]);

    const [isLoading, setIsLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [isDeleteAccountDialogOpen, setIsDeleteAccountDialogOpen] = useState(false);
    const [isDeleteAccountRedirecting, setIsDeleteAccountRedirecting] = useState(false);
    const deleteAccountAllowed = Boolean(context.environment?.features?.deleteAccountAllowed);

    const refreshCredentials = async () => {
        try {
            const credentialsData = await getCredentials({ context });
            setCredentials(credentialsData);
        } catch (error) {
            console.error('Failed to refresh credentials:', error);
        }
    };

    usePromise(
        async (signal) => {
            const data = await getCredentials({ signal, context });
            setIsDataLoading(false);
            return data;
        },
        setCredentials
    );

    useEffect(() => {
        if (!isDataLoading) {
            notifyAccountReady();
        }
    }, [isDataLoading]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const resetDeleteAccountState = () => {
            setIsDeleteAccountRedirecting(false);
            setIsDeleteAccountDialogOpen(false);
        };

        window.addEventListener("pageshow", resetDeleteAccountState);
        window.addEventListener("popstate", resetDeleteAccountState);

        return () => {
            window.removeEventListener("pageshow", resetDeleteAccountState);
            window.removeEventListener("popstate", resetDeleteAccountState);
        };
    }, []);

    // Helper function to format date
    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleDateString();
    };

    const resolveTranslatedMessage = (value?: string) => {
        if (!value || value.includes("${")) {
            return undefined;
        }

        return t(value, value);
    };

    const getCredentialDisplayName = (container: CredentialContainer) => {
        const translatedDisplayName = resolveTranslatedMessage(container.displayName);
        if (translatedDisplayName) {
            return translatedDisplayName;
        }

        return t(`${container.type}-display-name`, container.type);
    };

    const getCredentialHelpText = (container: CredentialContainer) => {
        const translatedHelpText = resolveTranslatedMessage(container.helptext);
        if (translatedHelpText) {
            return translatedHelpText;
        }

        return t(`${container.type}-help-text`, "");
    };

    const getCredentialInstanceLabel = (container: CredentialContainer, index: number) => {
        const baseName = getCredentialDisplayName(container);
        const credentialCount = container.userCredentialMetadatas?.length ?? 0;

        if (credentialCount <= 1) {
            return baseName;
        }

        return `${baseName} ${index + 1}`;
    };



    // Helper function to get credential type icon
    const getCredentialIcon = (type: string) => {
        const typeKey = type.toLowerCase();

        if (typeKey.includes('password')) {
            return <Key className="w-5 h-5" />;
        } else if (typeKey.includes('otp') || typeKey.includes('totp')) {
            return <Smartphone className="w-5 h-5" />;
        } else if (typeKey.includes('webauthn') || typeKey.includes('fido')) {
            return <CreditCard className="w-5 h-5" />;
        } else if (typeKey.includes('recovery') || typeKey.includes('backup')) {
            return <FileText className="w-5 h-5" />;
        }

        return <CheckCircle className="w-5 h-5" />;
    };



    const handleCreateCredential = (createAction: string) => {
        // Redirect to Keycloak's authentication flow for credential setup
        context.keycloak.login({ action: createAction });
    };

    const handleUpdateCredential = (updateAction: string) => {
        // Redirect to Keycloak's authentication flow for credential update
        context.keycloak.login({ action: updateAction });
    };

    const handleRemoveCredential = async (credential: CredentialMetadataRepresentation) => {
        if (!window.confirm(t("signingIn.remove.confirm", "Are you sure you want to remove this credential?"))) {
            return;
        }

        setIsLoading(true);
        try {
            // This would typically call a remove API - for now we'll show a placeholder
            // In a real implementation, this would call the Keycloak admin API
            console.log(credential);
            showSuccessToast(t("signingIn.remove.success", "Credential removed successfully"));
            await refreshCredentials();
        } catch (error) {
            showErrorToast(t("signingIn.remove.error", "Failed to remove credential"));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteAccount = async () => {
        setIsDeleteAccountRedirecting(true);

        try {
            await context.keycloak.login({
                action: "delete_account",
            });
        } catch (error) {
            console.error("Failed to open delete account flow:", error);
            showErrorToast(
                t(
                    "signingIn.deleteAccount.error",
                    "Failed to open account deletion confirmation",
                ),
            );
            setIsDeleteAccountRedirecting(false);
            setIsDeleteAccountDialogOpen(false);
        }
    };

    // Group credentials by category
    const credentialsByCategory = credentials.reduce((acc, container) => {
        const category = container.category || 'other';
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(container);
        return acc;
    }, {} as Record<string, CredentialContainer[]>);

    const categoryOrder = ['basic-authentication', 'two-factor', 'passwordless', 'other'];

    return (
        <Page
            title={t("signingIn.title", "Security Settings")}
            description={t("signingIn.description", "Manage your authentication methods and security settings")}
            className="mt-4 p-4"
        >
            <div className={`space-y-6 async-content ${credentials.length > 0 ? 'loaded' : ''}`}>
                {isDataLoading ? (
                    <>
                        {/* Basic Authentication Skeleton */}
                        <div className="rounded-2xl border border-border-primary p-6 shadow-sm">
                            <div className="mb-6">
                                <TextSkeleton width="180px" height="28px" />
                                <div className="mt-2">
                                    <TextSkeleton width="320px" height="14px" />
                                </div>
                            </div>
                            <div className="space-y-4">
                                <CardSkeleton showAvatar={true} lines={2} showActions={true} />
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        {categoryOrder.map((categoryKey) => {
                            const categoryCredentials = credentialsByCategory[categoryKey];
                            if (!categoryCredentials || categoryCredentials.length === 0) return null;

                            const categoryName = categoryKey === 'basic-authentication'
                                ? t("signingIn.basicAuthentication", "Basic Authentication")
                                : categoryKey === 'two-factor'
                                    ? t("signingIn.twoFactor", "Two-Factor Authentication")
                                    : categoryKey === 'passwordless'
                                        ? t("signingIn.passwordless", "Passwordless")
                                        : t("signingIn.other", "Other");

                            return (
                                <div key={categoryKey} className="rounded-2xl border border-border-primary p-6 shadow-sm">
                                    <div className="border-l-4 border-accent-primary pl-4 mb-6">
                                        <h2 className="text-xl font-semibold text-text-primary">
                                            {categoryName}
                                        </h2>
                                        <p className="text-sm text-text-secondary mt-1">
                                            {categoryKey === 'basic-authentication' && t("signingIn.basicAuthentication.description", "Primary authentication methods like passwords")}
                                            {categoryKey === 'two-factor' && t("signingIn.twoFactor.description", "Additional security layers for your account")}
                                            {categoryKey === 'passwordless' && t("signingIn.passwordless.description", "Sign in without passwords using biometrics or security keys")}
                                            {categoryKey === 'other' && t("signingIn.other.description", "Additional authentication options")}
                                        </p>
                                    </div>

                                    <div className="space-y-4">
                                        {categoryCredentials.map((container) => (
                                            <div key={container.type}>
                                                {/* Show Add button if no credentials exist but createAction is available */}
                                                {(!container.userCredentialMetadatas || container.userCredentialMetadatas.length === 0) && container.createAction && (
                                                    <div className="rounded-2xl border border-border-primary p-4 sm:flex sm:items-center sm:justify-between sm:gap-4">
                                                        <div className="flex min-w-0 items-start gap-3 sm:flex-1">
                                                            <div className="mt-0.5 text-text-secondary">
                                                                {getCredentialIcon(container.type)}
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <h3 className="whitespace-nowrap text-sm font-medium text-text-primary">
                                                                    {getCredentialDisplayName(container)}
                                                                </h3>
                                                                {getCredentialHelpText(container) && (
                                                                    <p className="mt-1 text-xs text-text-secondary">
                                                                        {getCredentialHelpText(container)}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="mt-4 flex justify-center sm:mt-0 sm:w-44 sm:flex-shrink-0">
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={() => handleCreateCredential(container.createAction)}
                                                                leftIcon={<Plus className="w-4 h-4" />}
                                                                className="w-full justify-center"
                                                            >
                                                                {t("signingIn.add", "Add")}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* User Credentials - Show existing credentials */}
                                                {container.userCredentialMetadatas && container.userCredentialMetadatas.length > 0 && (
                                                    <div className="space-y-3">
                                                        {container.userCredentialMetadatas.map((credentialMeta, index) => (
                                                            <div key={credentialMeta.credential.id || index} className="rounded-2xl border border-border-primary p-4 shadow-sm sm:flex sm:items-center sm:justify-between sm:gap-4">
                                                                <div className="flex items-start gap-3 sm:flex-1">
                                                                    <div className="mt-0.5 text-text-tertiary">
                                                                        {getCredentialIcon(credentialMeta.credential.type)}
                                                                    </div>
                                                                    <div className="min-w-0 flex-1">
                                                                        <p className="text-sm font-medium text-text-primary">
                                                                            {credentialMeta.credential.userLabel ||
                                                                                getCredentialInstanceLabel(container, index)}
                                                                        </p>
                                                                        <p className="mt-1 text-xs text-text-tertiary">
                                                                            {t("signingIn.createdOn", "Created on")}: {formatDate(credentialMeta.credential.createdDate)}
                                                                        </p>
                                                                        {credentialMeta.infoMessage && (
                                                                            <p className="text-xs text-accent-primary mt-1">
                                                                                {resolveTranslatedMessage(credentialMeta.infoMessage) ?? credentialMeta.infoMessage}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="mt-4 flex flex-col items-center gap-2 sm:mt-0 sm:w-44 sm:flex-shrink-0">
                                                                    {/* Update Button */}
                                                                    {container.updateAction && (
                                                                        <Button
                                                                            variant="primary"
                                                                            size="sm"
                                                                            onClick={() => handleUpdateCredential(container.updateAction)}
                                                                            leftIcon={<Edit className="w-3 h-3" />}
                                                                            className="w-full justify-center text-xs"
                                                                        >
                                                                            {t("signingIn.update", "Change")}
                                                                        </Button>
                                                                    )}

                                                                    {/* Remove Button */}
                                                                    {container.removeable && (
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            onClick={() => handleRemoveCredential(credentialMeta)}
                                                                            disabled={isLoading}
                                                                            leftIcon={<Trash2 className="w-3 h-3" />}
                                                                            className="w-full justify-center border-error-300 text-error-700 hover:bg-error-50 focus:ring-error-500 text-xs"
                                                                        >
                                                                            {t("signingIn.remove", "Remove")}
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Warning Messages */}
                                                {container.userCredentialMetadatas?.some(meta => meta.warningMessageTitle) && (
                                                    <div className="mt-3">
                                                        {container.userCredentialMetadatas
                                                            .filter(meta => meta.warningMessageTitle)
                                                            .map((meta, index) => (
                                                                <div key={index} className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded-2xl">
                                                                    <div className="flex items-center">
                                                                        <AlertTriangle className="w-5 h-5 text-amber-400 mr-2" />
                                                                        <div>
                                                                            <p className="text-sm font-medium text-amber-800">
                                                                                {meta.warningMessageTitle}
                                                                            </p>
                                                                            {meta.warningMessageDescription && (
                                                                                <p className="text-sm text-amber-700 mt-1">
                                                                                    {meta.warningMessageDescription}
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Empty State */}
                        {credentials.length === 0 && (
                            <div className="rounded-2xl border border-border-primary p-8 shadow-sm">
                                <div className="text-center">
                                    <Shield className="w-12 h-12 mx-auto text-text-tertiary mb-4" />
                                    <h3 className="text-lg font-medium text-text-primary mb-2">
                                        {t("signingIn.noCredentials", "No authentication methods configured")}
                                    </h3>
                                    <p className="text-text-tertiary">
                                        {t("signingIn.noCredentials.description", "Contact your administrator to set up authentication methods")}
                                    </p>
                                </div>
                            </div>
                        )}

                        {deleteAccountAllowed && (
                            <div className="rounded-2xl border border-border-primary p-6 shadow-sm">
                                <div className="mb-6 border-l-4 border-accent-primary pl-4">
                                    <h2 className="text-xl font-semibold text-text-primary">
                                        {t("signingIn.accountDeletion", "Account Deletion")}
                                    </h2>
                                    <p className="mt-1 text-sm text-text-secondary">
                                        {t(
                                            "signingIn.accountDeletion.description",
                                            "Permanently remove your account, sign-in methods, and linked data.",
                                        )}
                                    </p>
                                </div>

                                <div className="rounded-2xl border border-border-primary p-4 sm:flex sm:items-center sm:justify-between sm:gap-4">
                                    <div className="flex min-w-0 items-start gap-3 sm:flex-1">
                                        <div className="mt-0.5 text-text-primary">
                                            <Trash2 className="h-5 w-5" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <h3 className="text-sm font-medium text-text-primary">
                                                {t(
                                                    "signingIn.deleteAccount.title",
                                                    "Delete account and all data",
                                                )}
                                            </h3>
                                            <p className="mt-1 text-xs text-text-secondary">
                                                {t(
                                                    "signingIn.deleteAccount.description",
                                                    "After confirmation, your profile and connected authentication data will be permanently deleted.",
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-4 flex justify-center sm:mt-0 sm:w-44 sm:flex-shrink-0">
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => setIsDeleteAccountDialogOpen(true)}
                                            leftIcon={<Trash2 className="h-4 w-4" />}
                                            className="w-full justify-center"
                                        >
                                            {t(
                                                "signingIn.deleteAccount.action",
                                                "Delete",
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <Dialog
                isOpen={isDeleteAccountDialogOpen}
                onClose={() => {
                    if (!isDeleteAccountRedirecting) {
                        setIsDeleteAccountDialogOpen(false);
                    }
                }}
                title={t("signingIn.deleteAccount.confirm.title", "Delete account?")}
                confirmText={t("delete", "Delete")}
                cancelText={t("common.cancel", "Cancel")}
                onConfirm={handleDeleteAccount}
                loading={isDeleteAccountRedirecting}
                loadingText={t(
                    "signingIn.deleteAccount.redirecting",
                    "Opening confirmation...",
                )}
                type="error"
                size="lg"
            >
                <div className="space-y-3">
                    <p>
                        {t(
                            "signingIn.deleteAccount.confirm.message",
                            "This action will open the final confirmation step for permanent account deletion.",
                        )}
                    </p>
                    <p className="font-medium text-error-700">
                        {t(
                            "deleteAccountWarning",
                            "This is irreversible. All your data will be permanently destroyed.",
                        )}
                    </p>
                </div>
            </Dialog>
        </Page>
    );
};
